import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const privacyLabel = { public: "Public", first_name: "First name only", anonymous: "Anonymous", private: "Private" };

function PrayerDetail({ prayer, onClose, onChanged }) {
  const [excerpt, setExcerpt] = useState(prayer.public_excerpt || prayer.prayer_text);
  const [displayName, setDisplayName] = useState(prayer.display_name || "");
  const [notes, setNotes] = useState(prayer.notes || "");
  const [serve, setServe] = useState(prayer.opportunity_to_serve || false);

  const act = async (action, extra = {}) => {
    try {
      await api.patch(`/admin/prayers/${prayer.id}`, { action, public_excerpt: excerpt, display_name: displayName, notes, opportunity_to_serve: serve, ...extra });
      toast(`Done: ${action.replace(/_/g, " ")}`);
      onChanged();
      onClose();
    } catch (e) {
      toast("Something went wrong.");
    }
  };

  const del = async () => {
    await api.delete(`/admin/prayers/${prayer.id}`);
    toast("Deleted.");
    onChanged(); onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="prayer-detail">
      <div className="absolute inset-0 bg-ink/40" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-paper h-full overflow-y-auto border-l border-ink/25">
        <div className="p-6 border-b border-ink/15 flex items-center justify-between sticky top-0 bg-paper z-10">
          <span className="mono-label">Prayer {prayer.prayer_number ? `No. ${String(prayer.prayer_number).padStart(6, "0")}` : "(unnumbered)"}</span>
          <button onClick={onClose} className="mono-label underline" data-testid="detail-close">Close</button>
        </div>
        <div className="p-6 space-y-6">
          {prayer.risk_flag && <div className="stamp">Flagged — review with care</div>}
          <div className="grid grid-cols-2 gap-4 mono-label">
            <div>Submitter<div className="font-body text-ink normal-case text-base tracking-normal">{prayer.submitter_name}</div></div>
            <div>Contact<div className="font-body text-ink normal-case text-base tracking-normal">{prayer.email}</div></div>
            <div>Category<div className="font-body text-ink normal-case text-base tracking-normal">{prayer.category}</div></div>
            <div>Location<div className="font-body text-ink normal-case text-base tracking-normal">{prayer.location || "—"}</div></div>
            <div>Privacy<div className="font-body text-ink normal-case text-base tracking-normal">{privacyLabel[prayer.privacy_level]}</div></div>
            <div>For<div className="font-body text-ink normal-case text-base tracking-normal">{prayer.for_whom === "self" ? "Themselves" : "Someone else"}</div></div>
            <div>Monday consent<div className="font-body text-ink normal-case text-base tracking-normal">{prayer.consent_monday ? "Yes" : "No"}</div></div>
            <div>Public consent<div className="font-body text-ink normal-case text-base tracking-normal">{prayer.consent_public ? "Yes" : "No"}</div></div>
          </div>

          <div>
            <span className="mono-label block mb-2">Original submission (never overwritten)</span>
            <div className="border border-ink/15 bg-paper-secondary p-4 font-body text-ink leading-relaxed whitespace-pre-wrap">{prayer.prayer_text}</div>
          </div>

          {prayer.practical_help && (
            <div>
              <span className="mono-label block mb-2">Practical help suggested</span>
              <p className="font-body text-ink leading-relaxed">{prayer.practical_help}</p>
              <label className="flex items-center gap-2 mt-3 cursor-pointer">
                <input type="checkbox" checked={serve} onChange={(e) => setServe(e.target.checked)} className="w-4 h-4 accent-ink" data-testid="detail-serve" />
                <span className="font-body text-ink-secondary">Designate as "An Opportunity to Be the Answer"</span>
              </label>
            </div>
          )}

          <label className="block">
            <span className="mono-label block mb-2">Public display name</span>
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="w-full bg-transparent border border-ink/25 px-3 py-2 font-body text-ink outline-none" data-testid="detail-displayname" />
          </label>

          <label className="block">
            <span className="mono-label block mb-2">Public excerpt (privacy-safe)</span>
            <textarea rows={5} value={excerpt} onChange={(e) => setExcerpt(e.target.value)} className="w-full bg-transparent border border-ink/25 px-3 py-2 font-body text-ink outline-none" data-testid="detail-excerpt" />
          </label>

          <label className="block">
            <span className="mono-label block mb-2">Moderator notes (private)</span>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full bg-transparent border border-ink/25 px-3 py-2 font-body text-ink outline-none" data-testid="detail-notes" />
          </label>

          <div className="grid grid-cols-2 gap-3 paper-rule pt-6">
            <button onClick={() => act("approve_vigil")} className="btn-solid" data-testid="action-approve-vigil">Approve for The Vigil</button>
            <button onClick={() => act("approve_private")} className="btn-ink" data-testid="action-approve-private">Approve privately</button>
            <button onClick={() => act("edit_excerpt")} className="btn-ink" data-testid="action-save-excerpt">Save excerpt only</button>
            <button onClick={() => act("hold")} className="btn-ink" data-testid="action-hold">Hold for review</button>
            <button onClick={() => act("reject")} className="btn-ink" data-testid="action-reject">Reject</button>
            <button onClick={() => act("archive")} className="btn-ink" data-testid="action-archive">Archive</button>
          </div>
          <button onClick={del} className="mono-label underline underline-offset-4 text-terracotta" data-testid="action-delete">Delete permanently</button>

          {prayer.history?.length > 0 && (
            <div className="paper-rule pt-6">
              <span className="mono-label block mb-3">History</span>
              {prayer.history.map((h) => (
                <div key={h.id} className="mono-label mb-1">{new Date(h.timestamp).toLocaleString()} · {h.action} · {h.admin_email}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminNewPrayers() {
  const [prayers, setPrayers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("new");

  const load = useCallback(() => {
    api.get("/admin/prayers", { params: { moderation_status: filter } }).then(({ data }) => setPrayers(data.prayers));
  }, [filter]);
  useEffect(load, [load]);

  const open = async (id) => {
    const { data } = await api.get(`/admin/prayers/${id}`);
    setSelected(data);
  };

  return (
    <div className="p-8 md:p-12">
      <p className="mono-label mb-3">Moderation Queue</p>
      <h1 className="font-heading text-4xl md:text-5xl text-ink leading-none">New Prayers</h1>
      <p className="font-body text-ink-secondary mt-4 leading-relaxed max-w-2xl">Nothing is published until you approve it. Read each with care.</p>

      <div className="flex gap-2 mt-8">
        {["new", "held", "rejected", "archived", "approved"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`mono-label px-3 py-2 ${filter === s ? "bg-ink text-paper" : "border border-ink/25"}`} data-testid={`filter-${s}`}>{s}</button>
        ))}
      </div>

      <div className="mt-8 space-y-px bg-ink/15 border border-ink/15">
        {prayers.map((p) => (
          <button key={p.id} onClick={() => open(p.id)} className="w-full text-left bg-paper p-6 hover:bg-paper-tertiary block" data-testid={`queue-item-${p.id}`}>
            <div className="flex items-center justify-between gap-4 mb-2">
              <span className="mono-label">{p.category} · {new Date(p.created_at).toLocaleDateString()}</span>
              {p.risk_flag && <span className="stamp">Flagged</span>}
            </div>
            <div className="font-heading text-2xl text-ink">{p.title} <span className="font-body text-base text-ink-tertiary">— {p.submitter_name}</span></div>
            <p className="font-body text-ink-secondary mt-1 line-clamp-2 leading-relaxed">{p.prayer_text}</p>
          </button>
        ))}
        {prayers.length === 0 && <div className="bg-paper p-12 text-center font-body text-ink-tertiary italic">Nothing here. The queue is quiet.</div>}
      </div>

      {selected && <PrayerDetail prayer={selected} onClose={() => setSelected(null)} onChanged={load} />}
    </div>
  );
}
